import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Button, ButtonBar } from '@frontend/design-system';
import { AudienceTray } from '../audience-tray/audience-tray';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { useCountFilterRecipients } from './use-count-filter-recipients';

type Props = {
  applyFilters: () => void;
  cancelFilters: () => void;
  resetFilters: () => void;
};

export const FiltersViewFooter = ({ applyFilters, cancelFilters, resetFilters }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { enableManualSelection, filters, trackingIdPrefix } = useAudienceTray();
  const { filterRecipientsCount, isError } = useCountFilterRecipients();

  const description = useMemo(() => {
    if (isError) {
      return t('Invalid Filter Criteria');
    }
    const hasMultipleFilters = Object.keys(filters).length > 1;
    if (hasMultipleFilters) {
      return t('Matches Your Filters', { count: filterRecipientsCount });
    }
    return t('Matches Your Filter', { count: filterRecipientsCount });
  }, [t, isError, filters, filterRecipientsCount]);

  return (
    <AudienceTray.Footer>
      <AudienceTray.Footer.RecipientCount count={filterRecipientsCount} description={description} isError={isError} />
      <AudienceTray.Footer.ActionsBar>
        <Button
          disabled={Object.keys(filters).length === 0}
          onClick={resetFilters}
          size='large'
          trackingId={`${trackingIdPrefix}-filter-reset-btn`}
          variant='tertiary'
        >
          {t('Reset')}
        </Button>
        <ButtonBar css={{ justifyContent: 'flex-end', padding: 0 }}>
          <Button
            onClick={cancelFilters}
            size='large'
            trackingId={`${trackingIdPrefix}-filter-back-btn`}
            variant='secondary'
          >
            {t('Back')}
          </Button>
          <Button
            disabled={filterRecipientsCount === undefined || filterRecipientsCount === 0 || isError}
            onClick={applyFilters}
            size='large'
            trackingId={`${trackingIdPrefix}-filter-apply-btn`}
          >
            {enableManualSelection ? t('Apply Filter') : t('Create Segment')}
          </Button>
        </ButtonBar>
      </AudienceTray.Footer.ActionsBar>
    </AudienceTray.Footer>
  );
};
